import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { connect, readContracts, switchChain, watchAccount, simulateContract, writeContract, disconnect, reconnect, getAccount, waitForTransactionReceipt, WriteContractErrorType, WriteContractReturnType, watchConnections, getConnectors, getBalance } from '@wagmi/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { formatEther, parseEther, formatUnits, Address } from 'viem';

import { PopupService } from './popup.service';
import { coinbaseConnector, config, metaMaskConnector, uriConnector, walletConnector, walletConnectorBW } from './config';
import { EventService } from './event.service';
import { SwapStatus, getBrowserName, getParamWithoutCookie } from '../shared/constants/app-enums';
import { environment } from '../../environments/environment';
import { WINDOW } from './window';
import { isPlatformBrowser } from '@angular/common';
import {
  bscUsdcContract,
  bscUsdtContract,
  ethUsdcContract,
  ethUsdtContract,
  getChainData,
  getPresaleContract,
  getUSDCContract,
  getUSDTContract,
  multicallBscContract,
  multicallEthContract,
  presaleContractBsc,
  presaleContractEth,
  skipIndexUpdate,
  stakeContract,
  token,
} from '../shared/constants/wallet-connect.const';
import { balanceData, stakeData, tokenomics } from '../shared/interfaces/wallet.interface';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class WalletConnectService {
  swapStatus = SwapStatus.not_started;
  walletAddress: string = '';
  oldWalletAddress: string = '';
  wagmiData: any;
  chainId: number = 1;
  localStep: number = 0;
  mode: string = '';

  presaleData: any;
  swapHash: string = '';
  refreshId: any;
  refreshRate: number = 5000;

  /* predefined values of the essential variables */

  balanceData: balanceData = {
    nativeBal: 0,
    usdtBal: 0,
    usdcBal: 0,
    ethTokenSold: 0,
    bscTokenSold: 0,
    totalTokensSold: 0,
    ethClaimable: 0,
    bscClaimable: 0,
    userClaimable: 0,
    polyTokenSold: 0,
    polyClaimable: 0,
    paused: false,
    currentStep: 0,
    usdRaised: 0,
    maxTokensToBuy: 0,
    oneEth: 0,
    oneBnb: 0,
    ethUsdt: 0,
    bnbUsdt: 0,
    polyUsdt: 0,
    onePoly: 0,
    ethBal: 0,
    bscBal: 0,
    bscUsdtBal: 0,
    ethUsdtBal: 0,
    bscUsdcBal: 0,
    ethUsdcBal: 0,
  };

  stakeData: stakeData = {
    userStaked: 0,
    totalStaked: 0,
    rewardPerRound: 0,
    userPoolPercent: 0,
    apy: 0,
    unlockTime: 0,
    userReward: 0,
    userBal: 0,
    isClaimEnabled: false,
    planId: 0,
  };

  tokenomics: tokenomics[] = [];
  tokenomics2: tokenomics[] = [];
  chains: any;
  config: any;
  saleToken = '';
  isSoldOut: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private _platformId: Object,
    @Inject(WINDOW) private window: Window,
    private http: HttpClient,
    private eventService: EventService,
    private popupService: PopupService,
    private transletService: TranslateService,
    private analyticsService: AnalyticsService,
  ) {
    if (window && isPlatformBrowser(this._platformId)) {
      /**
       * wagmi createConfig setup for auto connect feature
       * and available connectors of wagmi.
       */

      this.reconnectMode();

      /**
       * watchAccount function continuously monitors the user’s
       * connected network. Whenever user switched/changed its
       * current network, account. This function keeps tracking that data
       * and updates the current chain id, wallet address accordingly.
       */

      watchAccount(config, {
        onChange: (data) => {
          this.setData();
          // console.log('Account changed!', data)
        },
      });

      watchConnections(config, {
        onChange: (data) => {
          this.setData();
          // console.log('Account changed!', data)
        },
      });
    }
  }

  async reconnectMode() {
    const recentConnectorId = await config.storage?.getItem('recentConnectorId');
    // console.log('\n==== wallet recentConnectorId ====\n', recentConnectorId, '\n====');
    if (recentConnectorId) {
      this.analyticsService.windowDataLayer('connectWallet', 'start', 1, 1, 0, '', '', 0, 0, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep]?.tokenUSDT);
      await reconnect(config, {}).then(
        (success: any) => {
          // console.log('\n==== wallet reconnected ====\n', success, '\n====');
          this.setData();
        },
        (err: any) => {
          console.log('\n==== wallet reconnection issue ====\n', err, '\n====');
          this.setData();
        },
      );
    } else {
      this.setData();
    }
  }

  /**
   * This function serves as the event listener for WalletConnect (without popup).
   * It should be invoked prior to the connect wallet function to listen for the "display_uri" event.
   * By leveraging this event, users can be directed to a specific BW app for wallet connection.
   */

  getListenWalletConnectEvent() {
    if (typeof window == 'undefined') return;
    const connectors = getConnectors(config);
    console.log('connectors', connectors);
    if (connectors.length > 0) {
      connectors.forEach((connector) => {
        connector.getProvider().then((provider: any) => {
          provider?.on('display_uri', (WalletUri: string) => {
            if (typeof window !== 'undefined') {
              const bwUrl = getParamWithoutCookie('bwUrl');
              const link = document.createElement('a');
              const urlParams = WalletUri + '&callbackUrl=' + window.location.href + '&browser=' + getBrowserName();
              link.href = environment.bwDeepLink + urlParams;
              if (bwUrl && bwUrl !== '') {
                link.href = bwUrl + urlParams;
                link.click();
              } else {
                // link.href = environment.bwUniversalLink + '/connect?' + urlParams;
                // link.target = '_blank';
                link.href = environment.bwDeepLink + urlParams;
                link.click();
              }
            }
          });
        });
      });
    }
  }

  /**
   * this is connect wallet function. The mode argument
   * is actually providing a choice to the app user whether
   * they want to connect via Metamask, WalletConnect V2 or web3Auth.
   * This function is mainly called from connect modal component.
   */

  async connectWallet(mode: string, isBsNetwork: boolean = false) {
    this.mode = mode;
    this.analyticsService.windowDataLayer('connectWallet', 'start', 1, 1, 0, '', '', 0, 0, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep].tokenUSDT);
    if (mode === 'bw') {
      this.getListenWalletConnectEvent();
    }

    await connect(config, {
      chainId: isBsNetwork ? environment.bsc.chainIdInt : environment.eth.chainIdInt,
      connector: mode === 'metamask' ? metaMaskConnector : mode === 'wallet' ? walletConnector : mode === 'walletBW' ? walletConnectorBW : mode === 'bw' ? uriConnector : mode === 'coinbase' ? coinbaseConnector : walletConnector,
    }).then(
      (success: any) => {
        console.log('\n==== wallet connected ====\n', success, '\n====');
        this.setData();
      },
      (err: any) => {
        console.log('\n==== wallet connection issue ====\n', err, '\n====');
      },
    );
  }

  /**
   * this is disconnect wallet function. After a successful disconnection
   * a developer can reset the different variables values.
   */

  async disConnectWallet() {
    await disconnect(config).then(
      (success: any) => {
        console.log('\n==== wallet disconnected ====\n');
        this.setData();
        this.analyticsService.referUrl = '';
        this.balanceData.nativeBal = 0;
        this.balanceData.usdtBal = 0;
        this.balanceData.usdcBal = 0;
      },
      (err: any) => {
        console.log('\n==== wallet disconnect issue ====\n');
        console.log('disconnect issue =', err);
      },
    );
  }

  /**
   * This function is getting called from watchAAccount.
   * Here we are saving the wagmi wallet address and
   * also calling the getPresale function for re-read
   * the smart contract function with new wallet address
   */

  setData(): void {
    this.wagmiData = getAccount(config);
    this.chainId = this.wagmiData.chainId;
    this.walletAddress = this.wagmiData.address ? this.wagmiData.address.toLowerCase() : '';
    this.refreshRate = this.wagmiData.address ? 10000 : 20000;
    this.eventService.isConnected = this.wagmiData.isConnected;
    this.getPresalesData();
    setTimeout(() => {
      this.eventService.setNetwork();
      if (this.wagmiData.address && this.wagmiData.address != this.oldWalletAddress) {
        this.oldWalletAddress = this.wagmiData.address;
        this.analyticsService.sendDashFx(this.walletAddress);
        this.analyticsService.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep].tokenUSDT);
      }
    }, 2000);
  }

  isEthChain(): boolean {
    return this.chainId === environment.eth.chainIdInt ? true : false;
  }

  /**
   * switching the current network. If user is on wrong network
   * and got error on switching then a popup
   * will appear saying to connect correct chain
   */
  async switchNetwork(chainId: number) {
    await switchChain(config, {
      chainId: chainId,
    }).then(
      (success: any) => {
      },
      (err: any) => {
        const chain = chainId == environment.eth.chainIdInt ? environment.eth : environment.bsc;
        const chainName = chain.chainInfo.params[0].chainName;
        this.popupService.messagePopup({ icon: 'info', desc: this.transletService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.transletService.instant('misc.ok') });
      },
    );
  }

  /**
   * In thiss fnctiion we are calling the different method
   * of smart contract to get different values
   */

  async getPresalesData() {
    const smartContract = {
      contracts: [
        // =========== Staking Contract ==============
        {
          ...stakeContract,
          functionName: 'poolStakers',
          chainId: environment.eth.chainIdInt, // 0
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...stakeContract,
          functionName: 'getRewards',
          chainId: environment.eth.chainIdInt, // 1
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...stakeContract,
          functionName: 'lockedTime',
          chainId: environment.eth.chainIdInt, // 2
          args: [],
        },
        {
          ...stakeContract,
          functionName: 'rewardTokensPerBlock',
          chainId: environment.eth.chainIdInt, // 3
          args: [],
        },
        {
          ...stakeContract,
          functionName: 'tokensStaked',
          chainId: environment.eth.chainIdInt, // 4
          args: [],
        },
        {
          ...stakeContract,
          functionName: 'harvestLock',
          chainId: environment.eth.chainIdInt, // 5
          args: [],
        },

        // =========== ETH Contract ==============
        {
          ...presaleContractEth,
          functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
          chainId: environment.eth.chainIdInt, // 6
          args: [],
        },
        {
          ...presaleContractEth,
          functionName: 'ethBuyHelper', // this method is returning the amount of bnb required for 1 app token.
          chainId: environment.eth.chainIdInt, // 7
          args: [1],
        },
        {
          ...presaleContractEth,
          functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
          chainId: environment.eth.chainIdInt, // 8
          args: [1],
        },
        {
          ...presaleContractEth,
          functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
          chainId: environment.eth.chainIdInt, // 9
          args: [],
        },
        {
          ...presaleContractEth, // 10 MAx token allowed to buy
          functionName: 'maxTokensToBuy',
          chainId: environment.eth.chainIdInt,
          args: [],
        },

        // =========== BSC Contract ==============

        {
          ...presaleContractBsc,
          functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
          chainId: environment.bsc.chainIdInt, // 11
          args: [],
        },
        {
          ...presaleContractBsc,
          functionName: 'bnbBuyHelper', // this method is returning the amount of bnb required for 1 app token.
          chainId: environment.bsc.chainIdInt, // 12
          args: [1],
        },
        {
          ...presaleContractBsc,
          functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
          chainId: environment.bsc.chainIdInt, // 13
          args: [1],
        },
        {
          ...presaleContractBsc,
          functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
          chainId: environment.bsc.chainIdInt, // 14
          args: [],
        },
        {
          ...presaleContractEth,
          functionName: 'currentStep',
          chainId: environment.eth.chainIdInt, // 15
          args: [],
        },
        {
          ...presaleContractEth,
          functionName: 'roundDetails',
          chainId: environment.eth.chainIdInt, // 16 ROUND END DATES
          args: [2],
        },
        {
          ...presaleContractEth, // 17
          functionName: 'trackRemainingTokens',
          chainId: environment.eth.chainIdInt,
          args: [],
        },
        {
          ...presaleContractBsc, // 18
          functionName: 'trackRemainingTokens',
          chainId: environment.bsc.chainIdInt,
          args: [],
        },
        {
          ...presaleContractBsc,
          functionName: 'userDeposits', // this method is returning the total token purchased by current user via BSC network.
          chainId: environment.bsc.chainIdInt, // 19
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...presaleContractEth,
          functionName: 'userDeposits', // this method is returning the total token purchased by current user via ETH network.
          chainId: environment.eth.chainIdInt, // 20
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...multicallBscContract,
          functionName: 'getEthBalance', // this method is returning the Bnb balance of user via multicall contract.
          chainId: environment.bsc.chainIdInt, // 21
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...multicallEthContract,
          functionName: 'getEthBalance', // this method is returning the eth balance of user via multicall contract.
          chainId: environment.eth.chainIdInt, // 22
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...bscUsdtContract,
          functionName: 'balanceOf', // this method is returning the usdt balance on BSC network.
          chainId: environment.bsc.chainIdInt, // 23
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...ethUsdtContract,
          functionName: 'balanceOf', // this method is returning the usdt balance on ETH network.
          chainId: environment.eth.chainIdInt, // 24
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...presaleContractEth, // To get the token address dynamically
          functionName: 'saleToken', //25
          chainId: environment.eth.chainIdInt,
          args: [],
        },
        {
          ...bscUsdcContract,
          functionName: 'balanceOf', // this method is returning the usdc balance on BSC network.
          chainId: environment.bsc.chainIdInt, // 26
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...ethUsdcContract,
          functionName: 'balanceOf', // this method is returning the usdc balance on ETH network.
          chainId: environment.eth.chainIdInt, // 27
          args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
        },
        {
          ...presaleContractEth,
          functionName: 'roundDetails',
          chainId: environment.eth.chainIdInt, //28 MAX TOKENS
          args: [0],
        },
        {
          ...presaleContractEth,
          functionName: 'roundDetails',
          chainId: environment.eth.chainIdInt, //29 TOKEN USDT
          args: [1],
        },
      ],
    } as const;

    /**
     * Above contracts are going to be read.
     */

    this.presaleData = await readContracts(config, smartContract);
    // console.log('\n******presaleData =', this.presaleData, '\n******\n');

    const currentStep = Number(this.presaleData[15].result);
    if (currentStep >= this.localStep) {
      this.balanceData.currentStep = currentStep;
      this.localStep = currentStep;
    } else {
      this.balanceData.currentStep = this.localStep;
    }
    this.stakeData.userStaked = +formatEther(this.presaleData[0].result[0] || 0);
    this.stakeData.unlockTime = Number(this.presaleData[0].result[1]) + Number(this.presaleData[2].result);

    // console.log("this.stakeData.unlockTime", +formatEther(this.presaleData[0].result[1] || 0), this.stakeData.unlockTime);

    this.stakeData.userReward = +formatEther(this.presaleData[1]?.result || 0);
    this.stakeData.rewardPerRound = +formatEther(this.presaleData[3].result || 0);
    this.stakeData.totalStaked = +formatEther(this.presaleData[4].result || 0);
    this.stakeData.isClaimEnabled = !this.presaleData[5].result || false;
    this.stakeData.apy = +((+environment.apyCalcConst / this.stakeData.totalStaked) * 100).toFixed(0);
    this.saleToken = this.presaleData[25].result;

    if (this.walletAddress) {
      this.balanceData.ethUsdtBal = +formatUnits(this.presaleData[24].result, 6);
      this.balanceData.bscUsdtBal = +formatUnits(this.presaleData[23].result, 18);
      this.balanceData.usdtBal = this.chainId == environment.eth.chainIdInt ? this.balanceData.ethUsdtBal : this.balanceData.bscUsdtBal;
      this.balanceData.ethBal = +formatEther(this.presaleData[22]?.result || 0);
      this.balanceData.bscBal = +formatEther(this.presaleData[21]?.result || 0);
      this.balanceData.nativeBal = this.chainId == environment.eth.chainIdInt ? this.balanceData.ethBal : this.balanceData.bscBal;
      this.balanceData.ethUsdcBal = +formatUnits(this.presaleData[27].result, 6);
      this.balanceData.bscUsdcBal = +formatUnits(this.presaleData[26].result, 18);
      this.balanceData.usdcBal = this.chainId == environment.eth.chainIdInt ? this.balanceData.ethUsdcBal : this.balanceData.bscUsdcBal;
    } else {
      this.balanceData.ethUsdtBal = 0;
      this.balanceData.bscUsdtBal = 0;
      this.balanceData.usdtBal = 0;
      this.balanceData.ethBal = 0;
      this.balanceData.bscBal = 0;
      this.balanceData.nativeBal = 0;
      this.balanceData.usdcBal = 0;
      this.balanceData.ethUsdcBal = 0;
      this.balanceData.bscUsdcBal = 0;
    }

    /* assigning the value of no of token sold va bsc and eth contract */
    this.balanceData.ethTokenSold = Number(this.presaleData[6].result);
    this.balanceData.bscTokenSold = Number(this.presaleData[11].result);
    this.balanceData.totalTokensSold = this.balanceData.ethTokenSold + this.balanceData.bscTokenSold;

    /* assigning the value of total USDT raised via eth bsc and poly */
    try {
      this.balanceData.oneEth = +formatEther(this.presaleData[7].result); // no of app tokens in exchange of 1 eth
      this.balanceData.ethUsdt = Number(this.presaleData[8].result); // no of app tokens in exchange of 1 Eth USDT
      this.balanceData.oneBnb = +formatEther(this.presaleData[12].result); // no of app tokens in exchange of 1 eth
      this.balanceData.bnbUsdt = Number(this.presaleData[13].result); // no of app tokens in exchange of 1 Eth USDT
    } catch (e) {
      console.log('Presale over');
    }
    this.balanceData.usdRaised = +formatEther(this.presaleData[9].result || 0) + +formatEther(this.presaleData[14].result || 0);

    /* assigning the value of total token purchased by the current user */
    this.balanceData.ethClaimable = +formatEther(this.presaleData[20].result || 0);
    this.balanceData.bscClaimable = +formatEther(this.presaleData[19].result || 0);
    this.balanceData.userClaimable = this.balanceData.ethClaimable + this.balanceData.bscClaimable;

    /**
     * Unsold token adjustment section.
     * if there is any mismatched from the block
     * chain end
     */

    const skipIndexEth: Array<any> = [];
    const skipIndexBsc: Array<any> = [];

    const ethUnsoldArr = skipIndexUpdate(this.presaleData[17].result, skipIndexEth);
    // ethUnsoldArr.splice(<index>, 0, <new value>);
    const bnbUnsoldArr = skipIndexUpdate(this.presaleData[18].result, skipIndexBsc);
    this.tokenomics2 = [];
    let newCounter = 0;
    for (let time of this.presaleData[16].result) {
      let minToken = newCounter == 0 ? 0 : Number(this.presaleData[28].result[newCounter - 1]);
      let maxToken = Number(this.presaleData[28].result[newCounter]);
      let tokenUSDT = +formatEther(this.presaleData[29].result[newCounter]);
      let roundInfo = {
        minToken,
        maxToken,
        maxAmount: newCounter == 0 ? (maxToken - minToken) * tokenUSDT : (maxToken - minToken) * tokenUSDT + this.tokenomics2[newCounter - 1].maxAmount,
        tokenUSDT,
        title: 'widget.round_title',
        endDate: Number(time) * 1000,
      };
      // console.log(roundInfo)
      if (newCounter < this.balanceData.currentStep) {
        const ethVal = ethUnsoldArr[newCounter] || 0;
        const bscVal = bnbUnsoldArr[newCounter] || 0;
        let roundUnSold = 1 * (roundInfo?.maxToken - roundInfo?.minToken) - (ethVal + bscVal);
        roundUnSold = roundUnSold * roundInfo.tokenUSDT;
        roundInfo.maxAmount = roundInfo.maxAmount + roundUnSold;
      }
      // roundInfo.maxAmount = Math.ceil(roundInfo.maxAmount)
      this.tokenomics2.push(roundInfo);
      newCounter++;
    }
    this.tokenomics = this.tokenomics2;
    // console.log("New Tokenomics ---------->",this.tokenomics)
    const tokenBuyLimit = Number(this.presaleData[10].result);
    this.balanceData.maxTokensToBuy = tokenBuyLimit;

    // console.log('tokenBuyLimit', tokenBuyLimit, this.tokenomics[this.balanceData.currentStep].maxToken, this.soldToken, this.balanceData.maxTokensToBuy )

    // console.log('\n******\n RPC balanceData =', this.balanceData, '\n******\n');
    // console.log('\n******\n RPC tokenomicsData =', this.tokenomics, '\n******\n');

    if (this.walletAddress && this.isSoldOut) this.getMetaData();
    if (this.refreshId) clearTimeout(this.refreshId);
    this.refreshId = setTimeout(() => this.getPresalesData(), this.refreshRate); // Call the self method on each 5secs interval
  }

  /**
   * get App token balance from the user's wallet.
   * This is for further stake from wallet balance
   */

  async getMetaData() {
    const tokenBalance = await getBalance(config, {
      address: this.walletAddress as any,
      token: this.saleToken as any,
      chainId: getChainData(this.chainId).chainIdInt,
    });

    /* user's Eth/Bnb and USDT balance */
    this.stakeData.userBal = +formatUnits(tokenBalance.value, tokenBalance.decimals);
  }

  /**
   * this function is swaping the native token with app token
   * @param amount: app token amount
   * @param value: eth token value
   */

  async swapNativeTokens(amount: number, value: number, isBuyStake: boolean = false) {
    // setting swapStatus to not started
    this.swapStatus = SwapStatus.not_started;

    /**
     * preparing a contract for swaping via smart contract
     * method of ethBuyHelper
     */

    const { request } = await simulateContract(config, {
      ...getPresaleContract(this.chainId),
      functionName: getChainData(this.chainId).nativeFunction,
      chainId: getChainData(this.chainId).chainIdInt,
      args: getChainData(this.chainId).hasStaking ? [amount, isBuyStake] : [amount],
      value: parseEther(`${value}`),
      // account:      this.walletAddress as Address    // Optional param. Not required!
    });

    console.log('simulateContract request =', request);

    // setting swapStatus to confirmation pending
    this.swapStatus = SwapStatus.confirm_pending;

    /**
     * Writing contract for swap process
     */
    await writeContract(config, request).then(
      (hash: any) => {
        console.log('Native write Contract success', hash);
        this.swapStatus = SwapStatus.in_progess;
        this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep].tokenUSDT);
        this.checkTransaction(hash, getChainData(this.chainId).purchaseToken, amount, value);
        // this.sendDashFx(getChainData(this.chainId).purchaseToken, amount, value);
      },
      (err: any) => {
        console.log('swapNativeTokens Error =', err);
        if (err.code === 4001) {
          this.swapStatus = SwapStatus.rejected;
        } else {
          this.swapStatus = SwapStatus.failed;
        }
      },
    );
  }

  /**
   * this function is checking allowence
   * amount before swaping the usdt token with app token
   * @param amount: app token amount
   * @param value: usdt token value
   * @param afterAllowance: checking whether allowence is granted or not
   */

  async swapCryptoForUSDT(amount: number, value: number, afterAllowance: boolean = false, isBuyStake: boolean = false, isUsdc: boolean = false) {
    /**
     * preparing token contract with ABI
     */
    const usdtContract = isUsdc ? getUSDCContract(this.chainId) : getUSDTContract(this.chainId);

    // set presale address based on current network
    const presaleAdd = getChainData(this.chainId).presaleAddress as Address;

    /**
     * reading allowence contract
     */

    const allowanceData = await readContracts(config, {
      contracts: [
        {
          ...usdtContract,
          functionName: 'allowance',
          chainId: this.chainId,
          args: [this.walletAddress, presaleAdd],
        },
      ],
    });

    //  checking provided allowence value in token contract

    let allowanceValue;
    if (getChainData(this.chainId) == environment.bsc) {
      allowanceValue = +formatEther(allowanceData[0].result as any);
    } else {
      allowanceValue = Number(allowanceData[0].result) / Math.pow(10, 6);
    }

    console.log('allowance value =', allowanceValue, '\nrequest value =', value);

    /**
     * if inserted usdt value is larger than
     * provided allowence value then it needs further approval.
     * below we are preparing contract for allowence approval
     */

    if (+value > +allowanceValue) {
      const { request } = await simulateContract(config, {
        ...usdtContract,
        chainId: this.chainId,
        functionName: 'approve',
        args: [presaleAdd, afterAllowance || allowanceValue == 0 ? '100000000000000000000000000' : '0'],
        account: this.walletAddress as Address, // Optional param. Not required!
      });

      // setting swapStatus to approval pending
      this.swapStatus = SwapStatus.approval_pending;

      // fetching transaction hash key of writing contract
      const hash = await writeContract(config, request);
      console.log('approve allowence hash key =', hash);

      /**
       * checking the approval transaction status.
       * if success and the allowence is preapproved
       * then it will call the buyUSDT function, else
       * will again go through the starting steps but this time with
       * allowence approval true mode.
       */
      await waitForTransactionReceipt(config, { hash }).then(
        (success: any) => {
          console.log('approve Config success', success);
          if (afterAllowance) {
            this.buyUSDT(amount, value, isBuyStake, isUsdc);
          } else {
            this.swapCryptoForUSDT(amount, value, true, isBuyStake, isUsdc);
          }
        },
        (err: any) => {
          console.log('approve Config error', err);
          this.swapStatus = SwapStatus.rejected;
        },
      );
    } else {
      /**
       * if inserted usdt value is smaller than
       * provided allowence value, then buyUSDT
       * function will be called and doesn't need
       * to check for allowence approval
       */
      this.buyUSDT(amount, value, isBuyStake, isUsdc);
    }
  }

  /**
   * this function is swaping the usdt token with app token
   * @param amount: app token amount
   * @param value: usdt token value
   * @param afterAllowance: checking whether allowence is granted or not
   */

  async buyUSDT(amount: number, value: number, isBuyStake: boolean = false, isUsdc: boolean = false) {
    /**
     * preparing a contract for swaping via smart contract
     * method of buyWithUSDT
     */
    let functionName = isUsdc ? 'buyWithUSDC' : 'buyWithUSDT';
    const contract = getPresaleContract(this.chainId);
    await simulateContract(config, {
      ...contract,
      functionName: functionName,
      chainId: this.chainId,
      args: getChainData(this.chainId).hasStaking ? [amount, isBuyStake] : [amount],
      // account: this.walletAddress as Address   // Optional param. Not required!
    })
      // if prepare contract is success
      .then(
        async (request: any) => {
          // setting swapStatus to confirmation pending
          console.log('usdtBuyHelper =', request);
          this.swapStatus = SwapStatus.confirm_pending;

          /**
           * Once confirmed startig write contract
           */
          await writeContract(config, request.request).then(
            (hash: WriteContractReturnType) => {
              console.log('USDT writeContract success', hash);
              this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep].tokenUSDT); // setting windowdatalayer for analytics
              this.swapStatus = SwapStatus.in_progess; // setting swapStatus to inprogress mode
              this.checkTransaction(hash, isUsdc ? 'usdc' : 'usdt', amount, value); // checking transaction status
              // this.sendDashFx('usdt', amount, value);                    // updating dashFx data
            },
            (error: WriteContractErrorType) => {
              console.log('USDT writeContract error', error);
              this.swapStatus = SwapStatus.rejected;
            },
          );
        },

        /**
         * if there is any error while preparing contract
         * like requesting more than max-token buy limit
         * then a warning popup will appear saying that
         * the max-token limitation
         */
        (err: any) => {
          this.popupService.messagePopup({ icon: 'warning', title: this.transletService.instant('widget.max_token_title'), desc: this.transletService.instant('widget.max_token_exceed', { maxTokensToBuy: this.balanceData.maxTokensToBuy }) });
          this.swapStatus = SwapStatus.rejected;
          console.log(err.message);
        },
      );
  }

  /**
   * this function is check the final
   * status of the current transaction
   * @param haskKey: transaction hash key
   * @param mode: Eth/Bnb/Poly/Usdt transaction
   * @param amount: no of tooken
   * @param value:  amount of eth/bnb/usdt
   */

  async checkTransaction(haskKey: string, mode: string, amount: number, value: number) {
    console.log('checkTransaction called');
    await waitForTransactionReceipt(config, { hash: haskKey as Address }).then(
      (response: any) => {
        console.log('checkTansaction success', response);
        this.swapStatus = SwapStatus.complete; // setting swapStatus to completed
        this.swapHash = response.transactionHash; // assigning succes transaction hash id
        this.analyticsService.sendBWData(mode, amount, value, this.walletAddress, this.swapHash, this.chainId, this.mode, this.balanceData, this.tokenomics[this.balanceData.currentStep].tokenUSDT);
        this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, mode, environment.tokenName, value, amount, this.walletAddress, this.swapHash, this.balanceData, this.chainId, this.tokenomics[this.balanceData.currentStep].tokenUSDT);
        this.analyticsService.bestWalletModal(this.mode);
      },
      (err: any) => {
        console.log('checkTansaction error', err);
        this.swapStatus = SwapStatus.failed; // setting swapStatus to failed
      },
    );
  }

  /**
   * Function to claim of purchased token by the user.
   * Once the presale stage is over, user can claim his token.
   * We are using eth based claim only. This function is
   * preparing a claim contract with a claim method.
   */

  async claim() {
    console.log('Claim called');
    this.swapStatus = SwapStatus.in_progess;
    const { request } = await simulateContract(config, {
      ...presaleContractEth,
      functionName: 'claim',
      chainId: environment.eth.chainIdInt,
      args: [],
      account: this.walletAddress as Address, // Optional param. Not required!
    });

    await writeContract(config, request).then(
      (success: any) => {
        console.log('Claim success', success);
        this.swapStatus = SwapStatus.complete;
      },
      (err: any) => {
        console.log('Claim error', err);
        this.swapStatus = SwapStatus.failed;
      },
    );
  }

  async claimAndStake(amount: number) {
    console.log('claimStake called');
    this.swapStatus = SwapStatus.in_progess;
    const { request } = await simulateContract(config, {
      ...presaleContractEth,
      functionName: 'claimAndStake',
      chainId: environment.eth.chainIdInt,
      args: [],
      account: this.walletAddress as Address, // Optional param. Not required!
    });
    await writeContract(config, request).then(
      (hash: any) => {
        console.log('claimStake success', hash);
        this.checkTransaction(hash, 'claimstake', 0, 0); // checking transaction status
      },
      (err: any) => {
        console.log('claimStake error', err);
        this.swapStatus = SwapStatus.failed;
      },
    );
  }

  /**
   * Function to claim and stake the token
   */

  async harvestRewards() {
    console.log('harvestRewards called');
    this.swapStatus = SwapStatus.in_progess;
    const { request } = await simulateContract(config, {
      ...stakeContract,
      functionName: 'harvestRewards',
      chainId: environment.eth.chainIdInt,
      args: [],
      account: this.walletAddress as Address, // Optional param. Not required!
    });

    await writeContract(config, request).then(
      (hash: any) => {
        console.log('harvestRewards success', hash);
        this.swapStatus = SwapStatus.complete;
      },
      (err: any) => {
        console.log('harvestRewards error', err);
        this.swapStatus = SwapStatus.failed;
      },
    );
  }

  /**
   * Function of extend the staking period.
   */

  async extendStake(planId: any) {
    console.log('extendStake called');

    const { request } = await simulateContract(config, {
      ...stakeContract,
      functionName: 'deposit',
      chainId: environment.eth.chainIdInt,
      args: [0, planId],
    });

    this.swapStatus = SwapStatus.confirm_pending;

    await writeContract(config, request).then(
      (hash: any) => {
        console.log('extendStake success', hash);
        this.swapStatus = SwapStatus.in_progess;
        this.checkTransaction(hash, '', 0, 0);
      },
      (err: any) => {
        console.log('extendStake error', err);
        this.swapStatus = SwapStatus.rejected;
      },
    );
  }

  async withdrawStake() {
    console.log('withdrawStake called');
    this.swapStatus = SwapStatus.in_progess;
    const { request } = await simulateContract(config, {
      ...stakeContract,
      functionName: 'withdraw',
      chainId: environment.eth.chainIdInt,
      args: [],
      account: this.walletAddress as Address, // Optional param. Not required!
    });
    await writeContract(config, request).then(
      (hash: any) => {
        console.log('withdrawStake success', hash);
        this.swapStatus = SwapStatus.complete;
      },
      (err: any) => {
        console.log('withdrawStake error', err);
        this.swapStatus = SwapStatus.failed;
      },
    );
  }

  async createStake(amount: any) {
    const allowanceData = await readContracts(config, {
      contracts: [
        {
          ...{
            abi: token.abi,
            address: this.saleToken as Address,
          },
          functionName: 'allowance',
          chainId: this.chainId,
          args: [this.walletAddress, environment.stakingAddress],
        },
      ],
    });
    console.log('createStake allowanceData =', allowanceData);

    const allowanceValue = +formatEther(allowanceData[0].result as any);
    const amountEther = +amount;
    console.log(amountEther, allowanceValue, amount);

    if (amountEther > +allowanceValue) {
      const { request } = await simulateContract(config, {
        ...{
          abi: token.abi,
          address: this.saleToken as Address,
        },
        chainId: this.chainId,
        functionName: 'approve',
        args: [environment.stakingAddress, '100000000000000000000000000'],
        account: this.walletAddress as Address, // Optional param. Not required!
      });

      console.log('approveConfig =', request);
      this.swapStatus = SwapStatus.approval_pending;

      try {
        const hash = await writeContract(config, request);
        console.log('createStake approveConfig hash =', hash);

        await waitForTransactionReceipt(config, { hash }).then(
          (success: any) => {
            console.log('approveConfig approve Config success', success);
            this.swapStatus = SwapStatus.confirm_pending;
            this.proceedCreateStake(amount);
          },
          (err: any) => {
            console.log('approve Config error', err);
            this.swapStatus = SwapStatus.rejected;
          },
        );
      } catch (error: any) {
        if (error.message.includes('User rejected')) {
          this.swapStatus = SwapStatus.rejected;
        } else {
          this.swapStatus = SwapStatus.failed;
        }
      }
    } else {
      this.proceedCreateStake(amount);
    }
  }

  async proceedCreateStake(amount: any) {
    const { request } = await simulateContract(config, {
      ...stakeContract,
      functionName: 'deposit',
      chainId: environment.eth.chainIdInt,
      args: [parseEther(amount.toString())],
    });

    this.swapStatus = SwapStatus.confirm_pending;
    await writeContract(config, request).then(
      (hash: any) => {
        console.log('proceedCreateStake success', hash);
        this.swapStatus = SwapStatus.in_progess;
        this.checkTransaction(hash, '', amount, 0);
      },
      (err: any) => {
        console.log('Eth createStake error', err);
        this.swapStatus = SwapStatus.rejected;
      },
    );
  }

  /**
   * Function to buy app token via wert widget.
   * this function convert the USDT value to app token
   * @param amount: Amount to spend
   * @returns : converted no of app tokens
   */

  getDynamicAmountWert = async (amount: number) => {
    console.log('getDynamicAmountWert', amount);
    const contractData = await readContracts(config, {
      contracts: [
        {
          ...presaleContractEth,
          functionName: 'ethBuyHelper',
          chainId: environment.eth.chainIdInt,
          args: [amount],
        },
      ],
    });
    let ehtVal = +formatEther(contractData[0].result as any);
    console.log('return DynamicAmountWert', ehtVal);
    return +ehtVal;
  };
}
